import { mapErrorToUserMessage } from '@repo/core';
/**
 * ─── Jewelry Rate Cards + Repairs + Customers ────────────────
 * Complete jewelry shop: rate cards, repair state machine,
 * customer CRM with KYC/AMLA.
 */

import { useEffect, useState, useCallback } from 'react'
import { Card, CardHeader, Badge, Button, Input, Modal } from '@repo/ui-core'
import { rateCardRepo, repairJobRepo, jewelryCustomerRepo } from '../../lib/db'
import {
  PriceCalculator,
  METAL_TYPE_LABELS, JOB_TYPE_LABELS, JOB_STATUS_LABELS, JOB_STATE_TRANSITIONS,
  LOYALTY_TIER_LABELS,
} from '@repo/entity-jewelry'
import { TrendingUp, Plus, Wrench, Users, CheckCircle, Clock, AlertCircle } from 'lucide-react'

// ═══════════════════════════════════════════════════════════════
// 1. Rate Cards Management
// ═══════════════════════════════════════════════════════════════

export function JewelryRatePage() {
  const [rates, setRates] = useState<any[]>([])
  const [loading, setLoading] = useState(true)
  const [showModal, setShowModal] = useState(false)
  const [saving, setSaving] = useState(false)
  const [form, setForm] = useState({ metalType: 'gold', purityKarat: 24, ratePerGram: 0, source: 'manual' })

  const load = useCallback(async () => {
    setLoading(true)
    try { const r = await rateCardRepo.findMany({ page: 1, pageSize: 100, sort: [{ field: 'effectiveAt', direction: 'desc' }] }); setRates('items' in r ? r.items : []) }
    catch (e) { console.error(e); alert(mapErrorToUserMessage(e).userMessage) } finally { setLoading(false) }
  }, [])
  useEffect(() => { load() }, [load])

  async function save() {
    setSaving(true)
    try {
      await rateCardRepo.create({ ...form, purityKarat: Number(form.purityKarat), ratePerGram: Number(form.ratePerGram), effectiveAt: Date.now(), tenantId: 'default', createdBy: 'admin' } as any)
      setShowModal(false); setForm({ metalType: 'gold', purityKarat: 24, ratePerGram: 0, source: 'manual' }); await load()
    } catch (e) { console.error(e); alert(mapErrorToUserMessage(e).userMessage) } finally { setSaving(false) }
  }

  const latestGold = rates.find((r: any) => r.metalType === 'gold' && r.purityKarat === 24)

  return (
    <div className="p-6">
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Rate Cards</h2>
          <p className="text-sm text-gray-500 mt-1">
            {latestGold ? `24K Gold: ₱${(latestGold.ratePerGram ?? 0).toLocaleString()}/gram` : 'No rates set'} 
            — Controls all item pricing via PriceCalculator
          </p>
        </div>
        <Button onClick={() => setShowModal(true)} icon={<Plus className="h-4 w-4" />}>Add Rate</Button>
      </div>
      <Card><CardHeader title="Daily Metal Rates" description="Metal price per gram — used by POS PriceCalculator" />
        <div className="overflow-x-auto"><table className="min-w-full divide-y divide-gray-200 text-sm">
          <thead className="bg-gray-50"><tr>{['Metal','Karat','Rate/gram','Effective','Source'].map(h=><th key={h} className="px-3 py-2.5 text-left text-xs font-medium uppercase text-gray-500">{h}</th>)}</tr></thead>
          <tbody className="divide-y divide-gray-200 bg-white">
            {loading ? <tr><td colSpan={5} className="py-8 text-center text-gray-400">Loading...</td></tr> :
             rates.length === 0 ? <tr><td colSpan={5} className="py-8 text-center text-gray-400">No rates. Add today's metal prices.</td></tr> :
             rates.map((r: any) => (
              <tr key={r.id} className="hover:bg-gray-50">
                <td className="px-3 py-2.5 font-medium">{METAL_TYPE_LABELS[r.metalType as keyof typeof METAL_TYPE_LABELS] ?? r.metalType}</td>
                <td className="px-3 py-2.5 text-center">{r.purityKarat}K</td>
                <td className="px-3 py-2.5 text-right font-bold text-green-700">₱{(r.ratePerGram ?? 0).toLocaleString()}</td>
                <td className="px-3 py-2.5 text-xs text-gray-500">{r.effectiveAt ? new Date(r.effectiveAt).toLocaleString() : '—'}</td>
                <td className="px-3 py-2.5 text-xs text-gray-400 capitalize">{r.source ?? 'manual'}</td>
              </tr>
            ))}
          </tbody>
        </table></div>
      </Card>
      <Modal open={showModal} onClose={() => setShowModal(false)} title="Add Rate Card" size="sm"
        footer={<div className="flex gap-2"><Button variant="secondary" onClick={() => setShowModal(false)}>Cancel</Button><Button onClick={save} loading={saving} icon={<TrendingUp className="h-4 w-4"/>}>Add Rate</Button></div>}>
        <div className="space-y-3">
          <div><label className="block text-sm font-medium text-gray-700 mb-1">Metal</label><select value={form.metalType} onChange={e => setForm({...form, metalType: e.target.value})} className="block w-full rounded-lg border border-gray-300 px-3 py-2 text-sm">{Object.entries(METAL_TYPE_LABELS).map(([k,v]) => <option key={k} value={k}>{v}</option>)}</select></div>
          <Input label="Karat" type="number" value={String(form.purityKarat)} onChange={e => setForm({...form, purityKarat: Number(e.target.value)})} />
          <Input label="Rate per Gram (₱)" type="number" value={String(form.ratePerGram)} onChange={e => setForm({...form, ratePerGram: Number(e.target.value)})} required />
        </div>
      </Modal>
    </div>
  )
}

// ═══════════════════════════════════════════════════════════════
// 2. Repair Jobs — State Machine
// ═══════════════════════════════════════════════════════════════

export function JewelryRepairPage() {
  const [jobs, setJobs] = useState<any[]>([])
  const [customers, setCustomers] = useState<any[]>([])
  const [loading, setLoading] = useState(true)
  const [showModal, setShowModal] = useState(false)
  const [saving, setSaving] = useState(false)
  const [form, setForm] = useState({ customerId: '', jobType: 'repair', description: '', estimatedCost: 0, promisedDays: 7, goldsmithName: '', workDescription: '', notes: '' })

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const [j, c] = await Promise.all([
        repairJobRepo.findMany({ page: 1, pageSize: 100, sort: [{ field: 'createdAt', direction: 'desc' }] }),
        jewelryCustomerRepo.findMany({ page: 1, pageSize: 500 }),
      ])
      setJobs('items' in j ? j.items : [])
      setCustomers('items' in c ? c.items : [])
    } catch (e) { console.error(e); alert(mapErrorToUserMessage(e).userMessage) } finally { setLoading(false) }
  }, [])
  useEffect(() => { load() }, [load])

  async function createJob() {
    setSaving(true)
    try {
      const customer = customers.find((c: any) => c.id === form.customerId)
      const promisedDate = Date.now() + Number(form.promisedDays) * 86400000
      await repairJobRepo.create({
        ...form, estimatedCost: Number(form.estimatedCost),
        customerName: customer?.fullName ?? '', jobNumber: 'RJ-' + Date.now().toString(36).toUpperCase().slice(-6),
        status: 'received', receivedDate: Date.now(), promisedDate,
        customAttributes: {}, tenantId: 'default', createdBy: 'admin', updatedBy: 'admin',
      } as any)
      setShowModal(false); setForm({ customerId: '', jobType: 'repair', description: '', estimatedCost: 0, promisedDays: 7, goldsmithName: '', workDescription: '', notes: '' }); await load()
    } catch (e) { console.error(e); alert(mapErrorToUserMessage(e).userMessage) } finally { setSaving(false) }
  }

  async function advanceStatus(job: any, newStatus: string) {
    const allowed = JOB_STATE_TRANSITIONS[job.status as keyof typeof JOB_STATE_TRANSITIONS]
    if (!allowed || !allowed.includes(newStatus as any)) {
      alert(`Invalid transition: ${job.status} → ${newStatus}. Allowed: ${allowed?.join(', ') ?? 'none'}`)
      return
    }
    try {
      const updates: any = { status: newStatus, updatedBy: 'admin' }
      if (newStatus === 'completed' || newStatus === 'delivered') updates.completedDate = Date.now()
      await repairJobRepo.update(job.id, updates)
      await load()
    } catch (e) { console.error(e); alert(mapErrorToUserMessage(e).userMessage) }
  }

  const statusIcon = (s: string) => s === 'ready' ? CheckCircle : s === 'in_progress' ? Clock : s === 'cancelled' ? AlertCircle : Wrench

  return (
    <div className="p-6">
      <div className="mb-6 flex items-center justify-between">
        <div><h2 className="text-2xl font-bold text-gray-900">Repairs & Custom Orders</h2><p className="text-sm text-gray-500 mt-1">{jobs.length} jobs — State machine: received → appraised → in_progress → ready → delivered</p></div>
        <Button onClick={() => setShowModal(true)} icon={<Plus className="h-4 w-4" />}>New Job</Button>
      </div>
      <div className="mb-6 grid grid-cols-2 gap-3 sm:grid-cols-5">
        {['received','appraised','in_progress','ready','delivered'].map(s => {
          const count = jobs.filter((j: any) => j.status === s).length
          const Icon = statusIcon(s)
          return (
            <div key={s} className={`rounded-xl border p-4 ${s === 'ready' ? 'border-green-200 bg-green-50' : 'border-gray-200 bg-white'}`}>
              <div className="flex items-center gap-2"><Icon className="h-4 w-4 text-gray-500" /><p className="text-xs text-gray-500 capitalize">{s.replace('_',' ')}</p></div>
              <p className="mt-1 text-xl font-bold">{count}</p>
            </div>
          )
        })}
      </div>
      <Card><CardHeader title="Job Orders" />
        <div className="overflow-x-auto"><table className="min-w-full divide-y divide-gray-200 text-sm">
          <thead className="bg-gray-50"><tr>{['Job #','Customer','Type','Description','Est. Cost','Status','Promise Date','Next Step'].map(h=><th key={h} className="px-3 py-2.5 text-left text-xs font-medium uppercase text-gray-500">{h}</th>)}</tr></thead>
          <tbody className="divide-y divide-gray-200 bg-white">
            {loading ? <tr><td colSpan={8} className="py-8 text-center text-gray-400">Loading...</td></tr> :
             jobs.length === 0 ? <tr><td colSpan={8} className="py-8 text-center text-gray-400">No repair jobs.</td></tr> :
             jobs.map((j: any) => {
               const nextStates = JOB_STATE_TRANSITIONS[j.status as keyof typeof JOB_STATE_TRANSITIONS] ?? []
               return (
              <tr key={j.id} className="hover:bg-gray-50">
                <td className="px-3 py-2.5 font-mono text-xs">{j.jobNumber}</td>
                <td className="px-3 py-2.5 text-xs font-medium">{j.customerName ?? j.customerId?.slice(0,8)}</td>
                <td className="px-3 py-2.5"><Badge color={j.jobType==='repair'?'blue':'purple'}>{JOB_TYPE_LABELS[j.jobType as keyof typeof JOB_TYPE_LABELS]??j.jobType}</Badge></td>
                <td className="px-3 py-2.5 text-xs max-w-[200px] truncate">{j.description}</td>
                <td className="px-3 py-2.5 text-right font-medium">₱{(j.estimatedCost??0).toLocaleString()}</td>
                <td className="px-3 py-2.5"><Badge color={j.status==='ready'?'green':j.status==='delivered'?'blue':j.status==='cancelled'?'red':'yellow'}>{JOB_STATUS_LABELS[j.status as keyof typeof JOB_STATUS_LABELS]??j.status}</Badge></td>
                <td className="px-3 py-2.5 text-xs text-gray-500">{j.promisedDate ? new Date(j.promisedDate).toLocaleDateString() : '—'}</td>
                <td className="px-3 py-2.5">
                  {nextStates.length > 0 && (
                    <div className="flex gap-1">
                      {nextStates.map(ns => (
                        <button key={ns} onClick={() => advanceStatus(j, ns)} className="text-xs px-2 py-1 rounded bg-blue-50 text-blue-700 hover:bg-blue-100">{JOB_STATUS_LABELS[ns as keyof typeof JOB_STATUS_LABELS] ?? ns}</button>
                      ))}
                    </div>
                  )}
                </td>
              </tr>
             )})}
          </tbody>
        </table></div>
      </Card>
      <Modal open={showModal} onClose={() => setShowModal(false)} title="New Repair / Custom Order" size="md"
        footer={<div className="flex gap-2"><Button variant="secondary" onClick={() => setShowModal(false)}>Cancel</Button><Button onClick={createJob} loading={saving} icon={<Wrench className="h-4 w-4"/>}>Create Job</Button></div>}>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <div className="sm:col-span-2"><label className="block text-sm font-medium text-gray-700 mb-1">Customer</label><select value={form.customerId} onChange={e => setForm({...form, customerId: e.target.value})} className="block w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"><option value="">Select...</option>{customers.map((c:any)=><option key={c.id} value={c.id}>{c.fullName} ({c.phone})</option>)}</select></div>
          <div><label className="block text-sm font-medium text-gray-700 mb-1">Type</label><select value={form.jobType} onChange={e => setForm({...form, jobType: e.target.value})} className="block w-full rounded-lg border border-gray-300 px-3 py-2 text-sm">{Object.entries(JOB_TYPE_LABELS).map(([k,v])=><option key={k} value={k}>{v}</option>)}</select></div>
          <Input label="Estimated Cost (₱)" type="number" value={String(form.estimatedCost)} onChange={e => setForm({...form, estimatedCost: Number(e.target.value)})} />
          <div className="sm:col-span-2"><Input label="Description" value={form.description} onChange={e => setForm({...form, description: e.target.value})} required /></div>
          <Input label="Goldsmith" value={form.goldsmithName} onChange={e => setForm({...form, goldsmithName: e.target.value})} />
          <Input label="Promise (days)" type="number" value={String(form.promisedDays)} onChange={e => setForm({...form, promisedDays: Number(e.target.value)})} />
          <div className="sm:col-span-2"><Input label="Work Description" value={form.workDescription} onChange={e => setForm({...form, workDescription: e.target.value})} /></div>
        </div>
      </Modal>
    </div>
  )
}

// ═══════════════════════════════════════════════════════════════
// 3. Customer CRM + KYC
// ═══════════════════════════════════════════════════════════════

export function JewelryCustomerPage() {
  const [customers, setCustomers] = useState<any[]>([])
  const [loading, setLoading] = useState(true)
  const [showModal, setShowModal] = useState(false)
  const [editId, setEditId] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)
  const [form, setForm] = useState({ firstName: '', lastName: '', phone: '', email: '', address: '', dateOfBirth: '', notes: '' })

  const load = useCallback(async () => {
    setLoading(true)
    try { const r = await jewelryCustomerRepo.findMany({ page: 1, pageSize: 500, sort: [{ field: 'createdAt', direction: 'desc' }] }); setCustomers('items' in r ? r.items : []) }
    catch (e) { console.error(e); alert(mapErrorToUserMessage(e).userMessage) } finally { setLoading(false) }
  }, [])
  useEffect(() => { load() }, [load])

  async function save() {
    setSaving(true)
    try {
      const p: any = { ...form, fullName: `${form.firstName} ${form.lastName}`.trim(), customerCode: 'CUST-' + Date.now().toString(36).toUpperCase().slice(-6), loyaltyTier: 'regular', totalPurchases: 0, totalOrders: 0, kycVerified: false, customAttributes: {}, tenantId: 'default', createdBy: 'admin', updatedBy: 'admin' }
      if (editId) await jewelryCustomerRepo.update(editId, { ...p, updatedBy: 'admin' })
      else await jewelryCustomerRepo.create(p)
      setShowModal(false); reset(); await load()
    } catch (e) { console.error(e); alert(mapErrorToUserMessage(e).userMessage) } finally { setSaving(false) }
  }

  function reset() { setForm({ firstName: '', lastName: '', phone: '', email: '', address: '', dateOfBirth: '', notes: '' }); setEditId(null) }
  function edit(c: any) { setForm({ firstName: c.firstName, lastName: c.lastName, phone: c.phone, email: c.email ?? '', address: c.address ?? '', dateOfBirth: c.dateOfBirth ?? '', notes: c.notes ?? '' }); setEditId(c.id); setShowModal(true) }

  async function toggleKyc(customer: any) {
    try {
      await jewelryCustomerRepo.update(customer.id, { kycVerified: !customer.kycVerified, updatedBy: 'admin' } as any)
      await load()
    } catch (e) { console.error(e); alert(mapErrorToUserMessage(e).userMessage) }
  }

  const amlaThreshold = 1000000

  return (
    <div className="p-6">
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Customers</h2>
          <p className="text-sm text-gray-500 mt-1">{customers.length} customers · KYC required for cash transactions ≥ ₱{amlaThreshold.toLocaleString()}</p>
        </div>
        <Button onClick={() => { reset(); setShowModal(true) }} icon={<Plus className="h-4 w-4" />}>Add Customer</Button>
      </div>
      <div className="mb-6 grid grid-cols-2 gap-3 sm:grid-cols-4">
        {[['Total',customers.length,'blue'],['KYC Verified',customers.filter((c:any)=>c.kycVerified).length,'green'],['Gold/Platinum',customers.filter((c:any)=>c.loyaltyTier==='gold'||c.loyaltyTier==='platinum').length,'purple'],['Total Purchases',`₱${customers.reduce((s:number,c:any)=>s+(c.totalPurchases??0),0).toLocaleString()}`,'amber']].map(([l,v,c]:any)=>(
          <div key={l} className={`rounded-xl border border-${c}-200 bg-${c}-50 p-4`}><p className={`text-xs text-${c}-600`}>{l}</p><p className={`mt-1 text-xl font-bold text-${c}-700`}>{v}</p></div>
        ))}
      </div>
      <Card><CardHeader title="Customer Registry" description="KYC/AMLA compliance — covered transaction threshold: ₱1,000,000" />
        <div className="overflow-x-auto"><table className="min-w-full divide-y divide-gray-200 text-sm">
          <thead className="bg-gray-50"><tr>{['Code','Name','Phone','Loyalty','Purchases','KYC',''].map(h=><th key={h} className="px-3 py-2.5 text-left text-xs font-medium uppercase text-gray-500">{h}</th>)}</tr></thead>
          <tbody className="divide-y divide-gray-200 bg-white">
            {loading ? <tr><td colSpan={7} className="py-8 text-center text-gray-400">Loading...</td></tr> :
             customers.length === 0 ? <tr><td colSpan={7} className="py-8 text-center text-gray-400">No customers.</td></tr> :
             customers.map((c: any) => (
              <tr key={c.id} className="hover:bg-gray-50">
                <td className="px-3 py-2.5 font-mono text-xs">{c.customerCode}</td>
                <td className="px-3 py-2.5 font-medium">{c.fullName}</td>
                <td className="px-3 py-2.5 text-gray-500 text-xs">{c.phone}</td>
                <td className="px-3 py-2.5"><Badge color={c.loyaltyTier==='platinum'?'purple':c.loyaltyTier==='gold'?'yellow':'blue'}>{LOYALTY_TIER_LABELS[c.loyaltyTier as keyof typeof LOYALTY_TIER_LABELS]??c.loyaltyTier}</Badge></td>
                <td className="px-3 py-2.5 text-right font-medium">₱{(c.totalPurchases??0).toLocaleString()}</td>
                <td className="px-3 py-2.5">
                  <button onClick={() => toggleKyc(c)} className={`text-xs px-2 py-1 rounded ${c.kycVerified ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                    {c.kycVerified ? '✓ Verified' : '✗ Unverified'}
                  </button>
                </td>
                <td className="px-3 py-2.5"><button onClick={() => edit(c)} className="text-xs text-blue-600 hover:underline">Edit</button></td>
              </tr>
            ))}
          </tbody>
        </table></div>
      </Card>
      <Modal open={showModal} onClose={() => { setShowModal(false); reset() }} title={editId ? 'Edit Customer' : 'Add Customer'} size="md"
        footer={<div className="flex gap-2"><Button variant="secondary" onClick={() => { setShowModal(false); reset() }}>Cancel</Button><Button onClick={save} loading={saving} icon={<Users className="h-4 w-4"/>}>{editId ? 'Update' : 'Create'}</Button></div>}>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <Input label="First Name" value={form.firstName} onChange={e => setForm({...form, firstName: e.target.value})} required />
          <Input label="Last Name" value={form.lastName} onChange={e => setForm({...form, lastName: e.target.value})} required />
          <Input label="Phone" value={form.phone} onChange={e => setForm({...form, phone: e.target.value})} required />
          <Input label="Email" value={form.email} onChange={e => setForm({...form, email: e.target.value})} />
          <Input label="Date of Birth" type="date" value={form.dateOfBirth} onChange={e => setForm({...form, dateOfBirth: e.target.value})} />
          <div className="sm:col-span-2"><Input label="Address" value={form.address} onChange={e => setForm({...form, address: e.target.value})} /></div>
        </div>
      </Modal>
    </div>
  )
}
